import React from 'react';
import { LocalizationProvider, DatePicker as MuiDatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import dayjs, { Dayjs } from 'dayjs';
import classNames from 'classnames';
import { useTheme } from 'src/providers/ThemeProvider';
import { Control, Controller } from 'react-hook-form';
import styles from './DatePicker.module.scss';
import Icon from '../Icon/Icon';

export interface DatePickerProps {
  label?: string;
  defaultValue: Dayjs | null;
  disabled?: boolean;
  onChange: (newValue: string | null) => void;
  error?: boolean;
  errorMessage?: string;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  shouldDisableDate?: (day: Dayjs) => boolean;
}

function formatDateToMicroseconds(date?: Date) {
  if (!date) return undefined;
  // Get the date and time components
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const milliseconds = String(date.getMilliseconds()).padStart(3, '0');

  // Convert milliseconds to microseconds (6 digits)
  const microseconds = milliseconds.padEnd(6, '0');

  // Build the formatted date string
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${microseconds}`;
}

const DatePicker: React.FC<DatePickerProps> = ({
  label,
  defaultValue,
  disabled,
  onChange,
  error,
  errorMessage,
  minDate,
  maxDate,
  shouldDisableDate,
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const { theme } = useTheme();
  const muiTheme = React.useMemo(
    () =>
      createTheme({
        palette: {
          mode: theme === 'dark' ? 'dark' : 'light',
          primary: { main: theme === 'dark' ? '#4A58C4' : '#2F3A8B' },
        },
        shape: { borderRadius: 8 },
      }),
    [theme],
  );
  const [date, setDate] = React.useState<Dayjs | null>(defaultValue);
  const handleSelect = (newValue: Dayjs | null) => {
    setDate(newValue);
    onChange(formatDateToMicroseconds(newValue?.toDate()) || null);
  };

  return (
    <div
      ref={containerRef}
      className={classNames(styles.root, { [styles.disabled]: disabled }, 'relative flex flex-col')}>
      {label && <span className="typo-text-14--bold text-faded mb-12">{label}</span>}
      <MuiThemeProvider theme={muiTheme}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <MuiDatePicker
            slotProps={{
              popper: {
                disablePortal: false,
                placement: 'bottom-start',
                modifiers: [
                  {
                    name: 'preventOverflow',
                    options: {
                      boundary: 'viewport',
                    },
                  },
                  {
                    name: 'flip',
                    options: {
                      fallbackPlacements: ['top-start', 'bottom-end', 'top-end'],
                    },
                  },
                ],
              },
            }}
            defaultValue={defaultValue}
            format="DD.MM.YYYY"
            value={date}
            disabled={disabled}
            minDate={minDate}
            maxDate={maxDate}
            shouldDisableDate={shouldDisableDate}
            onChange={handleSelect}
          />
        </LocalizationProvider>
      </MuiThemeProvider>
      {error && (
        <div className="flex align-center gap-x-4 mt-8 status-error-full">
          <Icon name="error" width={16} height={16} />
          <p className="typo-text-12--bold">{errorMessage}</p>
        </div>
      )}
    </div>
  );
};

interface ControlledDatePickerProps extends Omit<DatePickerProps, 'defaultValue' | 'onChange' | 'value'> {
  control: Control<any>;
  name: string;
  rules?: any;
}

const ControlledDatePicker: React.FC<ControlledDatePickerProps> = ({
  control,
  name,
  rules,
  error,
  errorMessage,
  ...rest
}) => {
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => (
        <DatePicker
          {...rest}
          defaultValue={field.value ? dayjs(field.value) : null}
          error={error ?? !!fieldState.error}
          errorMessage={errorMessage ?? fieldState.error?.message}
          onChange={(val: string | null) => field.onChange(val)}
        />
      )}
    />
  );
};

export { DatePicker, ControlledDatePicker };
